import {
  LabelId,
  type UnifiedSendEmailResponse,
  type UserCommunication,
} from '@this+that/models';

import { toTypedRecipients } from './request';
import type { DraftAccount } from '../../hooks/UserCommunications/draft/resolveEmailAccount';
import { UserCommunicationFilter } from '../../hooks/UserCommunications/filter/useFilterUserCommunications';
import type { ComposeSnapshot } from '../../models/composeSnapshot';
import { commitSavedDraftToStores } from '../../pages/private/Inbox/components/hooks/commitSavedDraft';
import type { DraftData } from '../../pages/private/Inbox/components/hooks/useSmartDraftManager';
import { useInboxEntriesStore } from '../../store/Inbox/InboxEntries';
import { useIntegrationsStore } from '../../store/Inbox/Integrations';
import { useUserCommunicationsStore } from '../../store/Inbox/UserCommunications';

const TAB_BY_CATEGORY_LABEL: Partial<Record<LabelId, UserCommunicationFilter>> =
  {
    [LabelId.CATEGORY_PROMOTIONS]: UserCommunicationFilter.PROMOTIONS,
    [LabelId.CATEGORY_FORUMS]: UserCommunicationFilter.FORUMS,
    [LabelId.CATEGORY_SOCIAL]: UserCommunicationFilter.SOCIAL,
    [LabelId.CATEGORY_UPDATES]: UserCommunicationFilter.UPDATES,
  };

export const deriveTabParam = (
  labelIds: LabelId[] | undefined,
): UserCommunicationFilter | undefined => {
  const category = labelIds?.find((label) => label in TAB_BY_CATEGORY_LABEL);
  return category ? TAB_BY_CATEGORY_LABEL[category] : undefined;
};

export const findSourceCommunication = (
  snapshot: ComposeSnapshot,
): UserCommunication | undefined => {
  const { getUserCommunicationByMessageId, getUserCommunicationByDraftId } =
    useUserCommunicationsStore.getState();
  return (
    (snapshot.sourceMessageId
      ? getUserCommunicationByMessageId(snapshot.sourceMessageId)
      : undefined) ??
    (snapshot.draftId
      ? getUserCommunicationByDraftId(snapshot.draftId)
      : undefined)
  );
};

interface CommitSyncedSnapshotInput {
  snapshot: ComposeSnapshot;
  data: DraftData;
  response: UnifiedSendEmailResponse;
  account: DraftAccount;
}

export const commitSyncedSnapshot = ({
  snapshot,
  data,
  response,
  account,
}: CommitSyncedSnapshotInput): void => {
  const source = findSourceCommunication(snapshot);
  const labelIds = source?.labelIds;
  const { upsertDraft } = useUserCommunicationsStore.getState();
  const { upsertDraftInInboxEntry } =
    useInboxEntriesStore.getState().taskManager.activeTaskGroup;

  commitSavedDraftToStores({
    draftId: response.messageId,
    platform: account.platform,
    messageRawText: response.messageRawText,
    draftVersion: response.draftVersion,
    pinnedAccountId: account.accountId,
    currentAccountDisplayName:
      useIntegrationsStore.getState().currentIntegrationAccount?.displayName,
    draftData: data,
    recipients: toTypedRecipients(data),
    handler: snapshot.handler,
    sourceUserCommunication: source,
    isInboxEmail: Boolean(labelIds?.includes(LabelId.INBOX)),
    isSentEmail: Boolean(labelIds?.includes(LabelId.SENT)),
    tabParam: deriveTabParam(labelIds),
    inboxEntryId: snapshot.inboxEntryId,
    actions: { upsertDraft, upsertDraftInInboxEntry },
  });
};
